import { NotificationType, UserNotification } from 'fitbeat.models';
import UserProfileService from './UserProfileService';
import { Platform } from 'react-native';

export default class NotificationManager {
  userProfileService: UserProfileService;

  constructor(userProfileService: UserProfileService, authProvider) {
    this.userProfileService = userProfileService;
    this.authProvider = authProvider;
  }

  async clearPushNotifications(): Promise<void> {
    return this.authProvider.removeAllDeliveredNotifications();
  }

  async isNotificationPresent(id: string, doNotRetry: boolean|undefined = undefined): Promise<boolean> {
    const notifications = this.getNotificationsSync();
    const isPresent = notifications
      .filter((notification: UserNotification) =>
        notification.id === id && !notification.read).length === 1;
    console.log('[NotificationManager] isNotificationPresent', { id, isPresent });
    if (!isPresent && doNotRetry !== false) {
      console.log('[NotificationManager] syncing profile and recheck isNotificationPresent');
      try {
        await this.userProfileService.syncData();
      } catch (e) { /* ignored */ }
      return await this.isNotificationPresent(id, false);
    }
    return isPresent;
  }

  async getUnreadNotificationCount(): Promise<number> {
    return this.getUnreadNotificationCountSync();
  }

  getUnreadNotificationCountSync(): number {
    const notifications = this.getNotificationsSync();
    return notifications.filter((notification: UserNotification) => !notification.read).length;
  }

  async getFirstNotification(): Promise<UserNotification | undefined> {
    const all = this.getNotificationsSync();
    if (all.length === 0) {
      console.log('no notifications');
      return undefined;
    }

    const sorted = all.sort((a: UserNotification, b: UserNotification) => a.created > b.created);
    const unread = sorted.filter((notification: UserNotification) => !notification.read);
    if (unread.length === 0) {
      console.log('no unread notifications, returing first');
      return all[0];
    }
    return unread[0];
  }

  getNotificationsSync(): UserNotification[] {
    try {
      const userProfile = this.userProfileService.currentUserProfile;
      const notifications = userProfile.notifications || [];
      return notifications;
    } catch (e) {
      console.error('failed to getNotificationsSync', e);
      return [];
    }
  }

  handleNotificationById(notifiationId: string, navigate): Promise<string> {
    const notifications = this.getNotificationsSync();
    const notification = notifications.filter((n: UserNotification) => n.id === notifiationId)[0];
    if (!notification) {
      console.warn('no such notification by id', notifiationId);
      return 'no_such_notification';
    }
    const result = this.handleNotification(notification, navigate);
    return result;
  }

  handleNotification(notification: UserNotification, navigate): Promise<string> {
    function unhandeldType() {
      // fallback to dumping the notification details
      console.warn(`unhandled notification type '${notification.type}', data: ${JSON.stringify(notification)}`);
    }

    function goToDashboard(type, day) {
      const midnightThisMorning = new Date().setHours(0, 0, 0, 0);
      if (new Date(day) < midnightThisMorning) {
        console.warn('[notifications] old dashboard-navigating notification is ignored', day, midnightThisMorning);
        return;
      }
      console.log('[notifications] navigating to dashboard', type, day);
      return navigate(Platform.OS === 'web' ? 'UserDashboard' : 'Dashboard', { day });
    }

    function goToDashboardNonSpecificDay() {
      if (Platform.OS === 'web') {
        // web needs the day set, so go to today
        return navigate('UserDashboard', { day: Date.now() });
      }
      return navigate('DashboardScreen', { });
    }

    function goToHome() {
      return navigate('HomeLoading');
    }

    function testNavigation(testType: number) {
      switch (parseInt(testType)) {
        case 0:
          alert('test notification');
          return;
        case 1:
          return navigate('CardCapture');
        case 3:
          const timestamp = Date.now() + 86400000;    // set the day to 24hours in future
          console.warn('test notification for tommorow' + timestamp);
          return goToDashboard('test', timestamp);
        case 4:
          const oldTimestamp = Date.now() - 86400000;    // set the day to 24hours in past
          console.warn('test notification for yesterday', oldTimestamp);
          return goToDashboard('test', oldTimestamp);
        default:
          console.warn(`Test notification: data=${JSON.stringify(notification.data)}`);
      }
    }

    console.log(`[notifications] handling notification ${notification.id}, ${notification.type}`);
    switch (notification.type) {
      case NotificationType.Test:
        return testNavigation(notification.data.random);
      case NotificationType.Invite:
      case NotificationType.InviteReturn:
      case NotificationType.DemeritReceived:
      case NotificationType.InviteChanged:
      case NotificationType.ReviewLastWorkout:
      case NotificationType.WaitlistOpened:
      case NotificationType.WaitlistClosed:
        return goToHome();
      default:
        unhandeldType();
    }
  }
}
